<template>
  <div class="send-quote-dialog-content">
    <!-- Info Section -->
    <div class="info-section">
      <v-icon color="#8585e0">mdi-information</v-icon>
      <div class="info">
        This email will be sent to TOPS2 to Create a Quote
      </div>
    </div>
    <!-- Comments Section -->

    <div class="comments-section">
      <label for="comments">Comments</label>
      <textarea
        id="comments"
        v-model="comments"
        placeholder="Add details"
      ></textarea>
    </div>
    <!-- Processing Options Section -->
    <div class="processing-options" v-if="config.processingOptions.markAsProcessed.visible">
        <p class="processing-options-title mt-2">Processing Options</p>
        <div class="checkbox-group">
          <v-checkbox v-model="markAsProcessed" :label="config.processingOptions.markAsProcessed.label" color="#003f2d"
            hide-details density="compact" class="custom-checkbox"></v-checkbox>
        </div>
      </div>
      <IsLoadingComponent v-if="showLoading" />
  </div>
</template>

<script>
import IconProvider from '../commons/IconProvider.vue';
import AlertNotification from '../commons/AlertNotification.vue';
import { markEmailAsCompleteNew, sendEmail } from '../../service/Actions.js';
import { mapActions , mapGetters, mapState } from 'vuex';
import processingOptions from '../../mixins/processingOptions.js';
import { handleRecentActions } from "../../service/engagement.js";
import { ACTION_TYPES } from '@/constants';
import IsLoadingComponent from '../IsLoadingComponent.vue';
import { CircleChevronRight } from 'lucide-vue-next';

export default {
  name: 'SendQuoteDialogContent',
  components: {
   IconProvider,
   AlertNotification,
   IsLoadingComponent
  },
  mixins: [processingOptions],
  props: {
    actionData: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object
    },
  },
  data() {
    return {
      comments: '',
      markAsProcessed: true,
      showLoading: false,
      engagementInProgressStatus: 'IN_PROGRESS'
    };
  },
  computed: {
    ...mapGetters({
      selectedAssignment: "assignments/selectedAssignment",
    }),
    ...mapState({
      currentEmail: (state) => state.email.currentEmail,
    }),
    isValidForSubmit() {
      // SendQuoteDialog is valid when there's an assignment and email selected
      return !!this.currentEmail;
    }
  },
  watch: {
    isValidForSubmit: {
      handler(newValue) {
        this.$emit('isValidForSubmit', newValue);
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      add: "notification/add",
      addMetricsData: "assignments/addMetricsData"
    }),
    async sendQuote() {
      //File ID/Assignement ID is not requied for "Create New Quote" 
      let metricsPayload = {
        entityId: this.selectedAssignment?.viewEventId,
        actionTaken: ACTION_TYPES.SEND_QUOTE_TO_ACTIVATE,
        status: 'Completed',
        engagementStatus: this.engagementInProgressStatus,
        engagementId: null,
        engagementNumber: null
      };
      try {
        this.showLoading = true;
        const item = Office.context.mailbox.item;
        if (!item) {
          this.showLoading = false;
          this.add({
          type: 'error',
          message: 'Failed to send the quote. Please try again.',
          timeout: 5
        });
          throw new Error('No email selected');
        }
        // Get the current email's subject and prepend "FW: " if it's not already there
        const subject = item.subject;
        const forwardSubject = subject.startsWith('FW:') ? subject : `FW: ${subject}`;
        const originalMessageId = this.currentEmail?.internetMessageId
        // Get the email body with comments
        const bodyContent = await new Promise((resolve, reject) => {
          Office.context.mailbox.item.body.getAsync(
            Office.CoercionType.Html,
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                // Create a properly formatted HTML email
                const commentsSection = this.comments 
                  ? `<div style="margin-bottom: 20px;"><p><strong>Comments:</strong> ${this.comments}</p><hr/></div>`
                  : '';
                const htmlContent = `
                  <!DOCTYPE html>
                  <html>
                    <body>
                      ${commentsSection}
                      ${result.value}
                    </body>
                  </html>`;
                resolve(htmlContent);
              } else {
                reject(new Error('Failed to get body'));
              }
            }
          );
        });

        // Get attachments and convert them to base64
        const attachments = await Promise.all((item.attachments || []).map(async (attachment) => {
          return new Promise((resolve, reject) => {
            Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve({
                  name: attachment.name,
                  contentBytes: result.value.content,
                  contentType: attachment.contentType,
                  isInline: attachment.isInline || false
                });
              } else {
                reject(new Error(`Failed to get attachment: ${attachment.name}`));
              }
            });
          });
        }));

        // Attempt to send the email
        await sendEmail(
          forwardSubject,
          bodyContent,
          process.env.VITE_SEND_TO_QUOTE, // Using the configured recipient
          attachments,
          {
            'X-TOPS2-Original-Message-Id': this.currentEmail?.internetMessageId
          },
          []
        );
        await this.saveRecentActions();
        // Only mark as complete and show success notification if email was sent successfully
        if (this.markAsProcessed) {
          // Mark email as complete with enhanced function - banner notification
          markEmailAsCompleteNew(
            'completeNotification',
            'Email Sent to TOPS2.',
            undefined,
            true
          );
        }
        await this.addMetricsData(metricsPayload);
        this.showLoading = false;
        this.add({
          type: 'success',
          message: 'Email Sent to TOPS2.',
          timeout: 5
        });
        
        // Close the dialog only on success
        this.closeDialog();
      } catch (error) {
        // Show error notification
        this.showLoading = false;
        metricsPayload.status = 'Error';
        metricsPayload.errorMessage = 'Failed to send to quote: ' + error.message;
        await this.addMetricsData(metricsPayload);
        this.add({
          type: 'error',
          message: 'Failed to send the quote. Please try again.',
          timeout: 5
        });
        console.error('Error sending quote:', error);
      }
    },
    closeDialog() {
      this.$emit('close');
    },
    async saveRecentActions() {
      const payload =  {
        action: ACTION_TYPES.SEND_QUOTE_TO_ACTIVATE,
        fileId: this.selectedAssignment?.code,
        engagementId: null,
        engagementStatus: this.engagementInProgressStatus,
        engagementNumber: null
      };
      await handleRecentActions(payload, ACTION_TYPES.SEND_QUOTE_TO_ACTIVATE);
    },
  },
};
</script>
<style scoped>
.send-quote-dialog {
  display: flex;
  flex-direction: column;
  overflow: auto; /* Prevent overflow outside the dialog */
}

.send-quote-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: auto;
  height: auto;
}

.info-section {
  display: flex;
  align-items: center;
  background-color: #e7e7f9;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  border: 1px solid #8585e0;
}

.info-section .info {
  margin-left: 8px;
  font-size: 14px;
}

.info-section .info-icon {
  color: #007bff;
}

.comments-section {
  margin-bottom: 16px;
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  vertical-align: middle;
  color: #000;
}

.comments-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.comments-section textarea {
  width: 100%;
  height: 120px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 12px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  max-height: 200px;
}

.quote-details-section,
.processing-options-section {
  margin-bottom: 0;
}

.quote-details-section h4,
.processing-options-section h4 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.dropdown-section label {
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
}

.dropdown-section select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.footer-section {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #ddd;
  background-color: #fff;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.cancel-button {
  background-color: #f8f9fa;
  border: 1px solid #ccc;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-button:hover {
  background-color: #e2e6ea;
}

.send-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.send-button:hover {
  background-color: #0056b3;
}

.send-button .send {
  margin-right: 8px;
}

.dialog-body {
  flex: 1;
  overflow-y: auto; /* Makes the body scrollable */
  padding: 0 16px 16px 16px;
}

.custom-checkbox {
  font-family: Calibre-R-Medium;
font-style: Regular;
vertical-align: middle;
font-size: 14px
}

.custom-checkbox :deep(label) {
  font-family: Calibre-R;
  font-size: 12px;
  margin-left: 8px;
}

.processing-options-title {
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  vertical-align: middle;
  color: #000;
}
</style>
